/**
 * Theme preference, resolution and storage — the pure half, with no React and
 * no DOM in it so it can be tested as arithmetic rather than as a component.
 */

/** What the operator asked for. `system` is the default and stays reachable. */
export type ThemePreference = 'system' | 'light' | 'dark';

/** What actually gets painted. There is no third answer. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * The localStorage key holding the preference.
 *
 * **This exact string is duplicated, once, in the inline `<script>` at the top
 * of index.html.** That script runs before the bundle is fetched — that is the
 * whole point of it — so it cannot import this constant, and the duplication is
 * the price of not flashing the wrong theme on every cold load. If you rename
 * this, rename it there too; the comment in index.html points back here.
 */
export const THEME_STORAGE_KEY = 'vv-backoffice-theme';

/** The media query the `system` preference follows. */
export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/** The class Tailwind's `darkMode: 'class'` looks for on `<html>`. */
export const DARK_CLASS = 'dark';

/**
 * Decide what to paint.
 *
 * `preference` is typed as a loose `string | null | undefined` rather than as
 * `ThemePreference` on purpose: every real caller gets it from
 * `localStorage.getItem`, which promises nothing about its contents. Typing the
 * parameter narrowly would move the validation to each call site, where it
 * would be written three times and forgotten once.
 */
export function resolveTheme(
    preference: string | null | undefined,
    systemPrefersDark: boolean,
): ResolvedTheme {
    if (preference === 'light' || preference === 'dark') return preference;
    return systemPrefersDark ? 'dark' : 'light';
}

/** Narrow a stored string to a preference, defaulting to `system`. */
export function normalizePreference(stored: string | null | undefined): ThemePreference {
    return stored === 'light' || stored === 'dark' ? stored : 'system';
}
